import { PcbLayoutFootprintPadShape, PrimitiveRole, PrimitiveType, staticLayoutLayerUids, } from "@buildwithflux/models";
import { geom2, mat3 } from "../../../common";
import { createShapeFromDxfString } from "../../../shapes/dxf/createShapeFromDxfString";
import { isDxfFileName } from "../../../shapes/helpers/isDxfFileName";
import { isSvgFileName } from "../../../shapes/helpers/isSvgFileName";
import { createShapeFromSvgString } from "../../../shapes/svg/createShapeFromSvgString";
import { createSvgShape } from "../../../shapes/svg/createSvgShape";
/** NOTE: For zones we don't spawn a shape in each layer, but we spawn a single on as metadata instead.
 * It will be consumer responsibility to associate them to layers using the connected layers property. */
export function createShapesForZone(node, stackup) {
    const metaLayer = stackup.find((l) => l.uid === staticLayoutLayerUids.metaLayerUid);
    if (!metaLayer) {
        return [];
    }
    const scale = node.bakedRules.scale;
    const transform = mat3.multiply(node.bakedRules.transformRelativeToShapesLayer ?? mat3.makeIdentity(), mat3.makeScale(scale ? scale.x : 1, scale ? scale.y : 1));
    const transformInverse = mat3.invert(transform);
    // If valid asset data is contained in bakedRules then attempt shape creation from that data - asset data takes
    // precedence in all cases
    if (node.bakedRules.asset?.payloadFileName && node.bakedRules.asset?.payloadAsBase64) {
        let threeShape = undefined;
        try {
            if (isSvgFileName(node.bakedRules.asset.payloadFileName)) {
                threeShape = createShapeFromSvgString(atob(node.bakedRules.asset.payloadAsBase64));
            }
            else if (isDxfFileName(node.bakedRules.asset.payloadFileName)) {
                threeShape = createShapeFromDxfString(atob(node.bakedRules.asset.payloadAsBase64), {
                    center: false,
                });
            }
        }
        catch (error) {
            // TODO: Ideally log error with flux logger - consider injecting flux logger into this function
            // eslint-disable-next-line no-console
            console.error(`Failed to create shape for zone:`, error);
        }
        if (!threeShape) {
            return [];
        }
        const points = threeShape.getPoints();
        return [
            {
                role: PrimitiveRole.zone,
                type: PrimitiveType.ApproxPolygon,
                points,
                layerId: metaLayer.uid,
                transform,
                transformInverse,
                convex: geom2.isConvexFromPoints(points),
            },
        ];
    }
    if (node.bakedRules.zoneShape === PcbLayoutFootprintPadShape.circular) {
        const isSquare = node.bakedRules.size.x === node.bakedRules.size.y;
        if (isSquare) {
            return [
                {
                    role: PrimitiveRole.zone,
                    type: PrimitiveType.Circle,
                    radius: node.bakedRules.size.x / 2,
                    layerId: metaLayer.uid,
                    transform,
                    transformInverse,
                },
            ];
        }
        else {
            return [
                {
                    role: PrimitiveRole.zone,
                    type: PrimitiveType.Oblong,
                    width: node.bakedRules.size.x,
                    height: node.bakedRules.size.y,
                    layerId: metaLayer.uid,
                    transform,
                    transformInverse,
                },
            ];
        }
    }
    if (node.bakedRules.zoneShape === PcbLayoutFootprintPadShape.rectangle) {
        const hasCornerRadius = node.bakedRules.cornerRadius &&
            (node.bakedRules.cornerRadius.topLeft > 0 ||
                node.bakedRules.cornerRadius.bottomLeft > 0 ||
                node.bakedRules.cornerRadius.topRight > 0 ||
                node.bakedRules.cornerRadius.bottomRight > 0);
        if (hasCornerRadius && node.bakedRules.cornerRadius) {
            return [
                {
                    role: PrimitiveRole.zone,
                    type: PrimitiveType.RoundedRectangle,
                    width: node.bakedRules.size.x,
                    height: node.bakedRules.size.y,
                    radiusTopLeft: node.bakedRules.cornerRadius.topLeft,
                    radiusBottomLeft: node.bakedRules.cornerRadius.bottomLeft,
                    radiusTopRight: node.bakedRules.cornerRadius.topRight,
                    radiusBottomRight: node.bakedRules.cornerRadius.bottomRight,
                    layerId: metaLayer.uid,
                    transform,
                    transformInverse,
                },
            ];
        }
        else {
            return [
                {
                    role: PrimitiveRole.zone,
                    type: PrimitiveType.Rectangle,
                    width: node.bakedRules.size.x,
                    height: node.bakedRules.size.y,
                    layerId: metaLayer.uid,
                    transform,
                    transformInverse,
                },
            ];
        }
    }
    const customPadShapeThree = createSvgShape(node.bakedRules.zoneShape);
    if (!customPadShapeThree)
        return [];
    const points = customPadShapeThree.getPoints();
    return [
        {
            role: PrimitiveRole.zone,
            type: PrimitiveType.ApproxPolygon,
            points,
            layerId: metaLayer.uid,
            transform,
            transformInverse,
            convex: geom2.isConvexFromPoints(points),
        },
    ];
}
//# sourceMappingURL=createPrimitivesForZone.js.map